import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {userRepresentativeRequestSchema} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {useUserRepresentativeService} from "../../services/user-representative-service";
import {useRepresentativeService} from "../../services/representative-service";
import {UserRepresentativeLink, UserRepresentativeMandate} from "../../utils/constants";
import AddEditRepresentativeModal from "./add-edit-representative-modal";

const AddEditUserRepresentativeModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [representativesList, setRepresentativesList] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userType, setUserType] = useState(1);
  const [reset, setReset] = useState(0);

  const addEditRepresentativeModal = useRef();

  let formSchema = userRepresentativeRequestSchema;

  let userRepresentativeService = useUserRepresentativeService();
  let representativeService = useRepresentativeService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null, userId= null, _userType=1) => {
    setLoading(true);

    if (!model && !userId){
      throw new Error('Userid or detail needed for AddEditUserRepresentativeModal');
    }

    let promises = [
      representativeService.getList()
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([representatives = [], detail= {}])=>{
      setRepresentativesList(representatives);

      setUserId(userId ? userId : detail.user_id);
      setUserType(_userType);

      let newDetail = Object.assign({}, detail);
      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return userRepresentativeService.getDetail(id);
  }

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...values};
    reqValues.user_id = userId;

    userRepresentativeService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  const onAddEditRepresentativeCallback = (item)=>{
    setRepresentativesList( [...HelperService.addOrReplaceItemInList(representativesList, item)]);
  };

  return (
    <>
      <AddEditRepresentativeModal ref={addEditRepresentativeModal} onSuccess={(item)=>onAddEditRepresentativeCallback(item)} />

      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue, values}) => (

          <Modal size="xl" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nové napojenie delegáta' : 'Úprava napojenia delegáta' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">

                  <FormGroup
                    name="representative_id"
                    type="select"
                    options={representativesList}
                    optionLabel="name"
                    optionKey="id"
                    placeholder="Delegát"
                    label="Delegát"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  >
                    <div className="text-center mt-1">
                      <span className='clickable hover-text-primary' onClick={()=>addEditRepresentativeModal.current.openModal(null)}>Vytvoriť nového delegáta</span>
                    </div>
                  </FormGroup>

                  <FormGroup
                    name="link_type"
                    type="select"
                    options={Object.values(UserRepresentativeLink)}
                    optionLabel="title"
                    optionKey="val"
                    placeholder="typ"
                    label="Typ"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="mandate_type"
                    type="select"
                    options={Object.values(UserRepresentativeMandate)}
                    optionLabel="title"
                    optionKey="val"
                    placeholder="typ"
                    label="Typ"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="scope"
                    placeholder="Rozsah zastúpenia"
                    label="Rozsah zastúpenia"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="procedure"
                    placeholder="spoločne / samostatne"
                    label="Spôsob konania"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="share"
                    placeholder="Podiel v percentách"
                    label="Podiel"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="clearfix"></div>
                  <hr />

                  <FormGroup
                    type="checkbox"
                    name="is_temporary"
                    placeholder="Iba dočasne"
                    label="Iba dočasne"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  {values?.is_temporary && <>
                    <FormGroup
                      name="starts_at"
                      type="datetime"
                      placeholder="Dátum začiatku platnosti"
                      label="Dátum začiatku platnosti"
                      className="col-md-3"
                      touched={touched} errors={errors} schema={formSchema}
                    />

                    <FormGroup
                      name="ends_at"
                      type="datetime"
                      placeholder="Dátum konca platnosti"
                      label="Dátum konca platnosti"
                      className="col-md-3"
                      touched={touched} errors={errors} schema={formSchema}
                    />
                  </>}

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditUserRepresentativeModal;
